import type { ClientMeasurementId, MeasurementProfile, MeasurementProfileId, SizeRange } from "./types.js";
import { childSizeCharts, childChartProfile, type ChildSizeChartId } from "./childSizeCharts.js";

/**
 * Standard sizes from the books, by body type. Every size fills the same
 * client measurement IDs every pattern reads, so a size picked here is only a
 * starting point: it is customised, saved as a profile, and each pattern then
 * adds its own ease.
 *
 * All figures are the books' healthy, athletic ones. Sizes for heavier or
 * slighter figures at the same heights are still to do (see the measurements
 * README).
 */
export type BodyType = "masculine" | "feminine" | "baby" | "child";

export interface BodyTypeInfo {
  id: BodyType;
  label: string;
  description: string;
  sizeRange: SizeRange;
  /** Saved example profiles shown with this body type's sizes. */
  examples: MeasurementProfileId[];
}

export const bodyTypes: BodyTypeInfo[] = [
  { id: "masculine", label: "Masculine", description: "Men's sizes by chest", sizeRange: "adult", examples: ["chris", "haydn"] },
  { id: "feminine", label: "Feminine", description: "Women's sizes by bust", sizeRange: "adult", examples: ["anca"] },
  { id: "baby", label: "Baby", description: "Birth to 2 years, by height", sizeRange: "baby", examples: [] },
  { id: "child", label: "Child", description: "3 to 14 years, by height", sizeRange: "child", examples: [] },
];

/** One size in a chart: its body measurements in cm. */
export interface ChartSize {
  id: string;
  label: string;
  /** Short note under the label, such as the age or chest range. */
  hint?: string;
  values: Partial<Record<ClientMeasurementId, number>>;
}

/** A height a chart can be adjusted to, with the change to each vertical measurement in cm. */
export interface ChartStature {
  id: "short" | "regular" | "tall";
  label: string;
  heightCm: number;
  adjust: Partial<Record<ClientMeasurementId, number>>;
}

export interface SizeChart {
  id: string;
  bodyType: BodyType;
  label: string;
  source: string;
  figure: "male" | "female";
  sizes: ChartSize[];
  /** The size a new body of this type starts from. */
  defaultSize: string;
  /** Adult charts only: heights the chart can be adjusted to (regular first). */
  statures?: ChartStature[];
}

/** Rows of a chart as columns of numbers, in the order of `sizes`. */
type Columns = Partial<Record<ClientMeasurementId, number[]>>;

function columns(ids: string[], labels: string[], rows: Columns, hints?: string[]): ChartSize[] {
  return ids.map((id, index) => ({
    id,
    label: labels[index],
    ...(hints?.[index] ? { hint: hints[index] } : {}),
    values: Object.fromEntries(Object.entries(rows).map(([row, values]) => [row, values![index]])),
  }));
}

const halve = (values: number[]) => values.map((value) => Math.round(value * 50) / 100);

// Aldrich, Metric Pattern Cutting for Menswear, 4th ed., p.11: young men,
// athletic figures of regular height (173–180 cm), 4 cm increments.
// Transcribed in reference/measurements/SBM-YM-Ath_Fig-4cm.png.
const mensChest = [88, 92, 96, 100, 104, 108, 112];
const mens4cm: Columns = {
  chest: mensChest,
  seat: [90, 94, 98, 102, 106, 110, 114],
  naturalWaist: [74, 78, 82, 86, 90, 94, 98],
  trouserWaist: [78, 82, 86, 90, 94, 98, 102],
  halfBack: [18.5, 19, 19.5, 20, 20.5, 21, 21.5],
  backNeckToWaist: [43, 43.4, 43.8, 44.2, 44.6, 44.8, 45],
  scyeDepth: [22, 22.8, 23.6, 24.4, 25.2, 25.6, 26],
  neck: [37, 38, 39, 40, 41, 42, 43],
  onePieceSleeveLength: [63, 63.6, 64.2, 64.8, 65.4, 65.7, 66],
  twoPieceSleeveLength: [79, 80, 81, 82, 83, 83.8, 84.6],
  insideLeg: [77, 78, 79, 80, 81, 82, 83],
  bodyRise: [27.2, 27.5, 27.8, 28.1, 28.4, 28.7, 29],
  wrist: [16.6, 17, 17.4, 17.8, 18.2, 18.6, 19],
  trouserBottomWidth: [21.4, 21.8, 22.2, 22.6, 23, 23.4, 23.8],
};

// The same book's small, medium, large and extra large (8 cm increments),
// reference/measurements/sm_md_lg_xl_8cm.png.
const mens8cm: Columns = {
  chest: [92, 100, 108, 116],
  seat: [94, 102, 110, 118],
  naturalWaist: [78, 86, 94, 102],
  trouserWaist: [82, 90, 98, 106],
  halfBack: [19, 20, 21, 22],
  backNeckToWaist: [43.4, 44.2, 45, 45.8],
  scyeDepth: [22.8, 24.4, 26, 27.6],
  neck: [38, 40, 42, 44],
  onePieceSleeveLength: [64, 65, 66, 67],
  twoPieceSleeveLength: [80, 82, 84, 86],
  insideLeg: [78, 80, 82, 84],
  bodyRise: [27.6, 28.2, 28.8, 29.4],
  wrist: [17, 17.8, 18.6, 19.4],
  trouserBottomWidth: [21.8, 22.6, 23.4, 24.2],
};

// Short (163–170 cm) and tall (183–190 cm) men: each vertical measurement adjusted.
const mensStatures: ChartStature[] = [
  { id: "regular", label: "Regular", heightCm: 176, adjust: {} },
  { id: "short", label: "Short", heightCm: 166, adjust: { backNeckToWaist: -2, scyeDepth: -1, onePieceSleeveLength: -2.5, twoPieceSleeveLength: -2.5, insideLeg: -4 } },
  { id: "tall", label: "Tall", heightCm: 186, adjust: { backNeckToWaist: 2, scyeDepth: 1, onePieceSleeveLength: 2.5, twoPieceSleeveLength: 2.5, insideLeg: 4, bodyRise: 1 } },
];

// Aldrich, Metric Pattern Cutting (women's wear), 4th ed., p.11: women of
// medium height (160–172 cm), sizes 8–26. Back width is the full width, so
// it is halved for halfBack; the book's "chest" is the front chest width.
// Transcribed in reference/books/aldrich-womenswear-4e/size-charts.md.
const womensUk: Columns = {
  bust: [80, 84, 88, 92, 96, 100, 104, 110, 116, 122],
  naturalWaist: [62, 66, 70, 74, 78, 82, 86, 92, 98, 104],
  seat: [86, 90, 94, 98, 102, 106, 110, 116, 122, 128],
  halfBack: halve([32.4, 33.4, 34.4, 35.4, 36.4, 37.4, 38.4, 39.8, 41.2, 42.6]),
  frontChestWidth: [30, 31.2, 32.4, 33.6, 34.8, 36, 37.2, 39, 40.8, 42.6],
  shoulder: [11.75, 12, 12.25, 12.5, 12.75, 13, 13.25, 13.6, 13.9, 14.2],
  neck: [35, 36, 37, 38, 39, 40, 41, 42.4, 43.8, 45.2],
  bustDart: [5.8, 6.4, 7, 7.6, 8.2, 8.8, 9.4, 10, 10.6, 11.2],
  armCircumference: [25.6, 27, 28.4, 29.8, 31.2, 32.4, 33.8, 35.8, 37.8, 39.8],
  wrist: [15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5],
  ankle: [23, 23.5, 24, 24.5, 25, 25.5, 26, 26.7, 27.4, 28.1],
  highAnkle: [20, 20.5, 21, 21.5, 22, 22.5, 23, 23.7, 24.4, 25.1],
  backNeckToWaist: [40, 40.5, 41, 41.5, 42, 42.5, 43, 43, 43, 43],
  frontShoulderToWaist: [40, 40.5, 41, 41.5, 42.3, 43.1, 43.9, 44.7, 45.5, 46.3],
  scyeDepth: [20, 20.5, 21, 21.5, 22, 22.5, 23, 23.7, 24.4, 25.1],
  waistToKnee: [57.5, 58, 58.5, 59, 59.5, 60, 60.5, 61, 61.5, 62],
  waistToHip: [20, 20.3, 20.6, 20.9, 21.2, 21.5, 21.8, 22.1, 22.4, 22.7],
  waistToFloor: [102, 103, 104, 105, 106, 107, 108, 109, 110, 111],
  bodyRise: [26.6, 27.3, 28, 28.7, 29.4, 30.1, 30.8, 31.8, 32.8, 33.8],
  onePieceSleeveLength: [57.5, 58, 58.5, 59, 59.5, 60, 60.5, 60.8, 61.1, 61.4],
  jerseySleeveLength: [51.5, 52, 52.5, 53, 53.5, 54, 54.5, 54.8, 55.1, 55.4],
  trouserBottomWidth: [21, 21.5, 22, 22.5, 23, 23.5, 24, 24.8, 25.6, 26.4],
};

// p.12: High Street fashion sizes, the flat-cutting blocks' default.
const womensHighStreet: Columns = {
  bust: [82, 88, 94, 100],
  naturalWaist: [64, 70, 76, 82],
  seat: [88, 94, 100, 106],
  halfBack: halve([32.8, 34.4, 36, 37.6]),
  frontChestWidth: [30.6, 32.4, 34.2, 36],
  shoulder: [11.8, 12.2, 12.6, 13],
  neck: [35.5, 37, 38.5, 40],
  bustDart: [6.1, 7, 7.9, 8.8],
  armCircumference: [26.4, 28.4, 30.4, 32.4],
  wrist: [15.3, 16, 16.7, 17.4],
  ankle: [23.1, 24, 24.9, 25.8],
  highAnkle: [20.1, 21, 21.9, 22.8],
  backNeckToWaist: [40.4, 41, 41.6, 42.2],
  frontShoulderToWaist: [40.4, 41, 41.6, 42.2],
  scyeDepth: [20.4, 21, 21.6, 22.2],
  waistToKnee: [57.7, 58.5, 59.3, 60.1],
  waistToHip: [20.2, 20.6, 21, 21.4],
  waistToFloor: [102.5, 104, 105.5, 107],
  bodyRise: [27, 28, 29, 30],
  onePieceSleeveLength: [57, 58, 59, 60],
  jerseySleeveLength: [51, 52, 53, 54],
};

// Short (152–160 cm) and tall (172–180 cm) women.
const womensStatures: ChartStature[] = [
  { id: "regular", label: "Medium", heightCm: 166, adjust: {} },
  { id: "short", label: "Short", heightCm: 156, adjust: { backNeckToWaist: -2, frontShoulderToWaist: -2, scyeDepth: -0.8, onePieceSleeveLength: -2.5, jerseySleeveLength: -2.5, waistToKnee: -3, waistToFloor: -5, bodyRise: -1 } },
  { id: "tall", label: "Tall", heightCm: 176, adjust: { backNeckToWaist: 2, frontShoulderToWaist: 2, scyeDepth: 0.8, onePieceSleeveLength: 2.5, jerseySleeveLength: 2.5, waistToKnee: 3, waistToFloor: 5, bodyRise: 1 } },
];

/** A children's chart's sizes, named by height, with the age printed under them. */
function childSizes(chartId: ChildSizeChartId): ChartSize[] {
  return childSizeCharts[chartId].heights.map((height) => {
    const profile = childChartProfile(chartId, height);
    // The description ends with the age when the chart prints one: "…, p. 37, about 3 years".
    const age = profile.description.match(/p\. \d+, (.+)$/)?.[1];
    return { id: String(height), label: `${height} cm`, ...(age ? { hint: age.replace(/^about /, "") } : {}), values: { ...profile.estimates, ...profile.values } };
  });
}

const ukSizes = ["8", "10", "12", "14", "16", "18", "20", "22", "24", "26"];
const letterSizes = ["S", "M", "L", "XL"];

export const sizeCharts: SizeChart[] = [
  { id: "mens-chest", bodyType: "masculine", label: "Chest size", source: "Aldrich menswear, p.11 (4 cm steps)", figure: "male", defaultSize: "100", statures: mensStatures,
    sizes: columns(mensChest.map(String), mensChest.map((chest) => `${chest}`), mens4cm, mensChest.map((chest) => `${Math.round(chest / 2.54)}″ chest`)) },
  { id: "mens-letter", bodyType: "masculine", label: "S M L XL", source: "Aldrich menswear (8 cm steps)", figure: "male", defaultSize: "M", statures: mensStatures,
    sizes: columns(letterSizes, letterSizes, mens8cm, ["88–96 chest", "96–104", "104–112", "112–120"]) },
  { id: "womens-uk", bodyType: "feminine", label: "UK size", source: "Aldrich women's wear, p.11", figure: "female", defaultSize: "12", statures: womensStatures,
    sizes: columns(ukSizes, ukSizes, womensUk, womensUk.bust!.map((bust) => `${bust} bust`)) },
  { id: "womens-letter", bodyType: "feminine", label: "S M L XL", source: "Aldrich women's wear, p.12 (High Street)", figure: "female", defaultSize: "M", statures: womensStatures,
    sizes: columns(letterSizes, letterSizes, womensHighStreet, ["size 8–10", "size 12", "size 14–16", "size 18"]) },
  { id: "babies", bodyType: "baby", label: "Baby", source: `Aldrich children's wear, p.${childSizeCharts.babies.page}`, figure: "male", defaultSize: "64", sizes: childSizes("babies") },
  { id: "girls", bodyType: "child", label: "Girls", source: `Aldrich children's wear, p.${childSizeCharts.girls.page}`, figure: "female", defaultSize: "122", sizes: childSizes("girls") },
  { id: "boys", bodyType: "child", label: "Boys", source: `Aldrich children's wear, p.${childSizeCharts.boys.page}`, figure: "male", defaultSize: "122", sizes: childSizes("boys") },
  { id: "girlsPlus", bodyType: "child", label: "Girls, plus", source: `Aldrich children's wear, p.${childSizeCharts.girlsPlus.page}`, figure: "female", defaultSize: String(childSizeCharts.girlsPlus.heights[0]), sizes: childSizes("girlsPlus") },
  { id: "boysPlus", bodyType: "child", label: "Boys, plus", source: `Aldrich children's wear, p.${childSizeCharts.boysPlus.page}`, figure: "male", defaultSize: String(childSizeCharts.boysPlus.heights[0]), sizes: childSizes("boysPlus") },
  { id: "girlsDeveloping", bodyType: "child", label: "Girls, developing", source: `Aldrich children's wear, p.${childSizeCharts.girlsDeveloping.page}`, figure: "female", defaultSize: String(childSizeCharts.girlsDeveloping.heights[0]), sizes: childSizes("girlsDeveloping") },
];

export function sizeChartsFor(bodyType: BodyType): SizeChart[] {
  return sizeCharts.filter((chart) => chart.bodyType === bodyType);
}

/** A chart size, adjusted to a stature, as a measurement profile. */
export function sizeChartProfile(chartId: string, sizeId: string, statureId: ChartStature["id"] = "regular"): MeasurementProfile | undefined {
  const chart = sizeCharts.find((item) => item.id === chartId);
  const size = chart?.sizes.find((item) => item.id === sizeId);
  if (!chart || !size) return undefined;
  const bodyType = bodyTypes.find((item) => item.id === chart.bodyType)!;
  if (bodyType.sizeRange !== "adult") {
    // Children's sizes keep the chart profile, with its head size for the mannequin.
    const profile = childChartProfile(chartId as ChildSizeChartId, Number(sizeId));
    return { ...profile, label: `${chart.label} ${size.label}` };
  }
  const stature = chart.statures?.find((item) => item.id === statureId) ?? chart.statures?.[0];
  const values = { ...size.values };
  for (const [id, change] of Object.entries(stature?.adjust ?? {}) as [ClientMeasurementId, number][]) {
    if (values[id] !== undefined) values[id] = Math.round((values[id]! + change) * 100) / 100;
  }
  if (stature) values.height = stature.heightCm;
  const tallness = stature && stature.id !== "regular" ? `, ${stature.label.toLowerCase()}` : "";
  return {
    label: `${chart.label === "S M L XL" ? "Size" : chart.label} ${size.label}${tallness}`,
    description: `${chart.source}${size.hint ? `, ${size.hint}` : ""}`,
    figure: chart.figure,
    sizeRange: bodyType.sizeRange,
    values,
  };
}

/** The body type a profile belongs to: by its size range, then its figure. */
export function bodyTypeForProfile(profile: Pick<MeasurementProfile, "figure" | "sizeRange">): BodyType {
  const range = profile.sizeRange ?? "adult";
  if (range === "baby") return "baby";
  if (range === "child") return "child";
  return profile.figure === "female" ? "feminine" : "masculine";
}
